import { createElement } from 'react';
import { GoalIcon } from 'lucide-react';

import { defineModule } from '@/shared/registry/types';

import { useGoalCommands, useGoalsNavCounter } from './components/extensionHooks';
import { LegacyGoalsRedirect } from './components/LegacyGoalsRedirect';
import { GoalSearchResult, MapSearchResult } from './components/SearchResults';
import { GOALS_PATH } from './api';
import { useGoalsTabTarget } from './model/lastMap';
import { LazyGoalPanel, LazyGoalsSection, LazyGroupMapsTab, LazyProfileGoalsTab } from './lazy';
import { registerGoalsRealtime } from './realtime';

/**
 * `goals` module — goal maps, a mind-map style planner (docs/modules/goals.md): the maps list is the «Цели» tab of the Tasks
 * page (a `pageSections` entry of `tasks`, `/tasks/goals`, with the count of the user's overdue goals), the canvas of a map has
 * the page `/tasks/goals/maps/:mapId` of its own (full layout), the old addresses `/goals/...` redirect there. The module has no
 * sidebar entry. Also the goal card panel `goal:<id>`, the group tab `maps`, the profile tab `goals`, ⌘K entries and the search
 * renderers of maps and goals.
 */
export const module = defineModule({
  id: 'goals',
  routes: [
    // The canvas of a map; the maps list is the goals tab of the Tasks page (below).
    {
      path: 'tasks/goals/maps/:mapId',
      handle: { layout: 'full' },
      lazy: async () => ({ Component: (await import('./pages/MapPage')).default }),
    },
    // The addresses of the time when the goals were a section of their own (bookmarks, notifications, search results).
    { path: 'goals/*', element: createElement(LegacyGoalsRedirect) },
  ],
  pageSections: [
    {
      page: 'tasks',
      id: 'goals',
      labelKey: 'goals:nav.title',
      icon: GoalIcon,
      order: 80,
      useCounter: useGoalsNavCounter,
      useTarget: useGoalsTabTarget,
      element: () => createElement(LazyGoalsSection),
    },
  ],
  sidePanels: [
    {
      type: 'goal',
      component: LazyGoalPanel,
      size: 'md',
      titleKey: 'goals:panel.title',
      // On the map the card sits beside the canvas: the map stays usable and a click on another goal swaps the card.
      modal: (pathname) => !pathname.startsWith(`${GOALS_PATH}/maps/`),
    },
  ],
  groupTabs: [
    {
      id: 'maps',
      labelKey: 'goals:group.tab',
      order: 55,
      element: (groupId) => createElement(LazyGroupMapsTab, { groupId }),
    },
  ],
  profileTabs: [
    {
      id: 'goals',
      labelKey: 'goals:profile.tab',
      order: 25,
      element: (userId) => createElement(LazyProfileGoalsTab, { userId }),
    },
  ],
  searchRenderers: { map: MapSearchResult, goal: GoalSearchResult },
  commands: [{ id: 'goals', headingKey: 'goals:commands.heading', order: 32, useItems: useGoalCommands }],
  setup: ({ realtime, queryClient }) => {
    registerGoalsRealtime(realtime, queryClient);
  },
});
